import { useCallback, useEffect, useRef } from "react";

import { PaymentMethodOption, WalletType } from "common/types";
import { toDollarValue } from "common/utils/formatting";
import { ClientCartDetail, DeliveryGroupKind, ICartItem } from "common/types/carts";
import { Optional } from "common/utils/types";
import { useIntersectionObserver } from "common/utils/dom";

import { ClientCheckoutOrder } from "client/orders/types";
import { INITIAL_CLIENT_DATA } from "client/common/utils";

import {
  AnalyticsCartItem,
  AnalyticsItemData,
  AnalyticsItemEventData,
  AnalyticsProductData,
  AnalyticsProductLineData,
  CustomAnalyticsEventName,
  CustomAnalyticsEventType,
  isAnalyticsItemData,
  isAnalyticsProductData,
  EcommerceAddToCartEvent,
  EcommerceAddShippingInfoEvent,
  EcommerceAddPaymentInfoEvent,
  EcommerceBeginCheckoutEvent,
  EcommerceEventName,
  EcommerceEventType,
  EcommerceItem,
  EcommercePaymentType,
  EcommerceSelectItemEvent,
  EcommerceViewItemEvent,
  EcommerceViewItemListEvent,
  EcommercePurchaseEvent,
  EcommerceRemoveFromCartEvent,
  PageViewData,
} from "../types";

export const getCartItemPricing = (cartItem: ICartItem): { price: number; discount: number } => ({
  price: cartItem.custom_unit_price ?? cartItem.default_unit_price,
  discount: cartItem.unit_discount,
});

const useV2 = INITIAL_CLIENT_DATA.use_v2_ecommerce_events;

/** Builds pricing fields from optional price/discount values. */
const pricingFields = (opts?: { price?: number; discount?: number }): Partial<EcommerceItem> => ({
  ...(opts?.price != null ? { price: toDollarValue(opts.price) } : {}),
  ...(opts?.discount != null ? { discount: toDollarValue(opts.discount) } : {}),
});

const v1ProductLineFields = (pl: AnalyticsProductLineData): Partial<EcommerceItem> => {
  const category = pl.primary_category_name ?? pl.categories?.[0]?.name;
  return {
    item_id: `pl_${pl.id}`,
    item_brand: pl.name,
    ...(category ? { item_category: category } : {}),
  };
};

const v1ProductFields = (product: AnalyticsProductData): Partial<EcommerceItem> => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { item_id: _itemId, ...plFields } = v1ProductLineFields(product.product_line);
  return {
    ...plFields,
    item_name: `${product.product_line.name} ${product.name}`,
  };
};

const v1ItemFields = (
  data: AnalyticsItemData,
  opts?: { item_variant?: string },
): Partial<EcommerceItem> => ({
  ...v1ProductFields(data.product),
  item_id: `${data.id}`,
  item_name: `${data.product.product_line.name} ${data.product.name}`,
  item_variant: opts?.item_variant ?? `${data.product.name} · ${data.name}`,
});

const v2ProductLineFields = (pl: AnalyticsProductLineData): Partial<EcommerceItem> => ({
  item_id: `${pl.id}`,
  item_name: pl.name,
  item_brand: pl.brand_name,
  item_category: pl.primary_category_name,
  item_category2: pl.categories
    .filter((c) => c.name !== pl.primary_category_name)
    .map((c) => c.name)
    .sort()
    .join(", "),
  prescription_required: pl.prescription_required,
  ...(pl.manufacturer ? { manufacturer_name: pl.manufacturer.name } : {}),
});

const v2ProductFields = (product: AnalyticsProductData): Partial<EcommerceItem> => ({
  ...v2ProductLineFields(product.product_line),
  product_id: `${product.id}`,
  product_name: product.name,
});

const v2ItemFields = (data: AnalyticsItemData): Partial<EcommerceItem> => ({
  ...v2ProductFields(data.product),
  vetcove_item_id: `${data.id}`,
  vetcove_item_name: data.name,
  days_supply: data.days_supply,
  frequency: `${data.duration_value} ${data.duration_unit}`,
});

const toEcommerceItem = (
  data: AnalyticsItemEventData,
  opts?: { price?: number; discount?: number; item_variant?: string },
): EcommerceItem => {
  if (useV2) {
    if (isAnalyticsItemData(data)) {
      return { ...v2ItemFields(data), ...pricingFields(opts) };
    }
    if (isAnalyticsProductData(data)) {
      return { ...v2ProductFields(data), ...pricingFields(opts) };
    }
    return { ...v2ProductLineFields(data), ...pricingFields(opts) };
  }

  if (isAnalyticsItemData(data)) {
    return { ...v1ItemFields(data, opts), ...pricingFields(opts) };
  }
  if (isAnalyticsProductData(data)) {
    return { ...v1ProductFields(data), ...pricingFields(opts) };
  }
  return { ...v1ProductLineFields(data), ...pricingFields(opts) };
};

/**
 * Converts an AnalyticsCartItem to EcommerceItem.
 * Uses pre-computed price and discount from the cart item.
 */
const analyticsCartItemToEcommerceItem = (cartItem: AnalyticsCartItem): EcommerceItem => ({
  ...toEcommerceItem(cartItem.item, { price: cartItem.price, discount: cartItem.discount }),
  quantity: cartItem.quantity,
});

const initialClientData = INITIAL_CLIENT_DATA;

/**
 * Sends GA4 ecommerce events via `gtag()`. Events are processed by the gtag.js
 * library into the eventModel format GA4 and GTM tags expect — including the
 * nested ecommerce item shape — so prefer this over `sendCustomEvent` for
 * anything GA4 recognizes natively.
 *
 * Assumes the partner has gtag.js fully loaded for the clinic, either by
 * providing a GA measurement ID (loaded by c15t in ConsentProvider) or by
 * loading gtag as a tag inside their Google Tag Manager container. The stub
 * in react-bundle.html queues calls made before gtag.js initializes; gtag.js
 * replays them once it loads. Clinics without either path will silently drop
 * these events.
 *
 * For custom (non-GA4) events — e.g. page_view virtual pageviews — use
 * `sendCustomEvent` instead, which pushes to the dataLayer directly and does
 * not require gtag.js.
 *
 * Event names and data are defined by our partners' GTM setup.
 * Keep consistent with: https://docs.google.com/document/d/1sYGNv_llIEIvoFTq2Z9fX12oGn3BT46NgAlDYM0LsI0/
 * */
export const sendEcommerceEvent = <T extends EcommerceEventType<EcommerceEventName, object>>({
  eventName,
  data,
}: T) => {
  if (initialClientData.debug_analytics_events) {
    console.log(`[Analytics] ${eventName}`, data); // eslint-disable-line no-console
  }
  gtag("event", eventName, data);
  if (initialClientData.has_internal_analytics_tracking) {
    // TODO: Uncomment once we want to enable analytics. Removed for now to help debug
    // FE performance issues across the app due to high memory usage.
    // Will also need to add back in the AnalyticsProvider, ClientAnalyticsProvider, and
    // ClinicAnalyticsProvider in ClientRouter.tsx and ClinicRouter.tsx
    // Checking out commit 850f420fc94e893b2aa1364056262c363451c7f4 will have all the files
    // needed to enable this again.
    // posthog.capture(eventName, data);
  }
};

/**
 * Sends custom (non-GA4) analytics events by pushing directly to the dataLayer.
 * The event name is prefixed with `vetcove_` (e.g. `page_view` →
 * `vetcove_page_view`) so it cannot collide with GA4 reserved event names or
 * other tags sharing the container.
 *
 * Unlike `sendEcommerceEvent`, this does not depend on gtag.js being loaded —
 * GTM reads the pushed object directly — making it the right choice for events
 * that partners wire up through GTM triggers rather than GA4 ecommerce.
 */
export const sendCustomEvent = <
  T extends CustomAnalyticsEventType<CustomAnalyticsEventName, object>,
>({
  eventName,
  data,
}: T) => {
  const namespacedEventName = `vetcove_${eventName}`;
  if (initialClientData.debug_analytics_events) {
    console.log(`[Analytics] ${namespacedEventName}`, data); // eslint-disable-line no-console
  }
  window.dataLayer.push({ event: namespacedEventName, ...data });
};

export const sendPageView = (data: PageViewData) => {
  sendCustomEvent({ eventName: CustomAnalyticsEventName.PageView, data });
};

export const sendPurchase = (order: ClientCheckoutOrder, cart: ClientCartDetail) => {
  const data: EcommercePurchaseEvent = {
    eventName: EcommerceEventName.Purchase,
    data: {
      currency: "USD",
      transaction_id: `${order.id}`,
      value: toDollarValue(order.discounted_subtotal),
      shipping: toDollarValue(order.shipping),
      tax: toDollarValue(order.tax),
      items: order.order_items.map((orderItem) => ({
        ...toEcommerceItem(orderItem.item, {
          price: orderItem.unit_price,
          discount: orderItem.amortized_discount,
        }),
        quantity: orderItem.quantity,
      })),
      coupon: cart.promo_codes.map((pc) => pc.code).join(", "),
    },
  };
  sendEcommerceEvent(data);
};

export const sendAddToCart = (cartItem: AnalyticsCartItem) => {
  const item = analyticsCartItemToEcommerceItem(cartItem);
  const data: EcommerceAddToCartEvent = {
    eventName: EcommerceEventName.AddToCart,
    data: {
      currency: "USD",
      value: toDollarValue(cartItem.price * cartItem.quantity),
      items: [item],
    },
  };
  sendEcommerceEvent(data);
};

export const sendRemoveFromCart = (cartItem: AnalyticsCartItem) => {
  const item = analyticsCartItemToEcommerceItem(cartItem);
  const data: EcommerceRemoveFromCartEvent = {
    eventName: EcommerceEventName.RemoveFromCart,
    data: {
      currency: "USD",
      value: toDollarValue(cartItem.price * cartItem.quantity),
      items: [item],
    },
  };
  sendEcommerceEvent(data);
};

export const sendViewItem = (
  data: AnalyticsItemEventData,
  opts: { price: number; item_variant?: string },
) => {
  const item = toEcommerceItem(data, opts);
  const eventData: EcommerceViewItemEvent = {
    eventName: EcommerceEventName.ViewItem,
    data: {
      currency: "USD",
      value: toDollarValue(opts.price),
      items: [item],
    },
  };
  sendEcommerceEvent(eventData);
};

export const sendViewItemList = (items: AnalyticsItemEventData[], listName: string) => {
  const data: EcommerceViewItemListEvent = {
    eventName: EcommerceEventName.ViewItemList,
    data: {
      item_list_name: listName,
      items: items.map((item, i) => ({
        ...toEcommerceItem(item),
        ...(useV2 ? { index: i } : {}),
      })),
    },
  };
  sendEcommerceEvent(data);
};

/**
 * Fires a `view_item_list` GA4 event when the referenced element scrolls into view.
 * The event re-fires whenever the set of item IDs or the list name changes.
 *
 * @returns A ref to attach to the container element that should trigger the event.
 */
export const useViewItemListOnVisible = (
  items: Optional<AnalyticsItemEventData[]>,
  listName: string,
) => {
  const ref = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  const itemKey = items?.map((item) => item.id).join(",") ?? "";

  useEffect(() => {
    firedRef.current = false;
  }, [itemKey, listName]);

  const onIntersect = useCallback(() => {
    if (!firedRef.current && items?.length) {
      firedRef.current = true;
      sendViewItemList(items, listName);
    }
  }, [items, listName]);
  useIntersectionObserver(ref, onIntersect);
  return ref;
};

/**
 * @param price — included for backwards compatibility with cart item clicks.
 *   TODO: Consider removing price for events where it doesn't make sense
 *   (e.g., product line card clicks, search result clicks).
 */
export const sendSelectItem = (data: AnalyticsItemEventData, listName: string, price?: number) => {
  const item = toEcommerceItem(data, price != null ? { price, discount: 0 } : undefined);
  const eventData: EcommerceSelectItemEvent = {
    eventName: EcommerceEventName.SelectItem,
    data: {
      item_list_name: listName,
      items: [item],
    },
  };
  sendEcommerceEvent(eventData);
};

/** Resolves ICartItem pricing and converts to EcommerceItem in one step. */
const cartItemToEcommerceItem = (cartItem: ICartItem): EcommerceItem => {
  const { price, discount } = getCartItemPricing(cartItem);
  return {
    ...toEcommerceItem(cartItem.item, { price, discount }),
    quantity: cartItem.quantity,
  };
};

export const sendBeginCheckout = (cart: ClientCartDetail) => {
  const items = cart.items.map(cartItemToEcommerceItem);
  const value = cart.items.reduce((sum, ci) => {
    const { price } = getCartItemPricing(ci);
    return sum + toDollarValue(price) * ci.quantity;
  }, 0);
  const data: EcommerceBeginCheckoutEvent = {
    eventName: EcommerceEventName.BeginCheckout,
    data: {
      currency: "USD",
      value,
      items,
    },
  };
  sendEcommerceEvent(data);
};

export const sendAddShippingInfo = (cart: ClientCartDetail) => {
  const coupon = cart.promo_codes.map((pc) => pc.code).join(", ");

  cart.delivery_groups
    .filter((g) => g.kind === DeliveryGroupKind.Shipped)
    .forEach((group) => {
      const option = group.selected_delivery_option;
      if (!option) {
        return;
      }

      const data: EcommerceAddShippingInfoEvent = {
        eventName: EcommerceEventName.AddShippingInfo,
        data: {
          currency: "USD",
          value: toDollarValue(cart.subtotal),
          ...(coupon ? { coupon } : {}),
          shipping_tier: option.title,
          items: group.cart_items.map((ci) => cartItemToEcommerceItem(ci)),
        },
      };
      sendEcommerceEvent(data);
    });
};

const getPaymentType = (paymentMethod: PaymentMethodOption): EcommercePaymentType => {
  if (paymentMethod.is_klarna) {
    return "klarna";
  }
  if (paymentMethod.wallet === WalletType.APPLE) {
    return "apple_pay";
  }
  if (paymentMethod.wallet === WalletType.GOOGLE) {
    return "google_pay";
  }
  return "credit_card";
};

export const sendAddPaymentInfo = (cart: ClientCartDetail, paymentMethod: PaymentMethodOption) => {
  const coupon = cart.promo_codes.map((pc) => pc.code).join(", ");

  const data: EcommerceAddPaymentInfoEvent = {
    eventName: EcommerceEventName.AddPaymentInfo,
    data: {
      currency: "USD",
      value: toDollarValue(cart.subtotal),
      ...(coupon ? { coupon } : {}),
      payment_type: getPaymentType(paymentMethod),
      items: cart.items.map((ci) => cartItemToEcommerceItem(ci)),
    },
  };
  sendEcommerceEvent(data);
};
